import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { ListItem } from '@shared/ast/astTypes'
import { getBlockText, getInlineText } from '@shared/ast/astUtils'
import { applyReadinessAnnotationChanges } from '@shared/ast/writingReadiness'
import {
  type WritingKind,
  analyzeWritingSyntax,
  inferSectionLevel,
  parseSectionOpening,
} from '@shared/ast/writingSyntax'
import {
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
  LexicalNode,
  TextNode,
} from 'lexical'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { lexicalToAst } from '../../../../transforms/ast'
import type { Annotation } from '../../../../types/annotations'
import { $createCustomHeadingNode, CustomHeadingNode } from '../nodes/CustomHeadingNode'
import { CustomParagraphNode } from '../nodes/CustomParagraphNode'
import './writingSyntax.css'

// Transforms run inside the current writable state; editor.getEditorState() still describes the previous commit.
function $snapshot() {
  const serialize = (node: LexicalNode): any => ({
    ...node.exportJSON(),
    ...($isElementNode(node) ? { children: node.getChildren().map(serialize) } : {}),
  })
  return lexicalToAst({ root: serialize($getRoot()) }, { lang: 'mixed', dir: 'rtl' })
}
const choices = [
  { label: 'מקטע', value: ' ', startOnly: true },
  ...[1, 2, 3, 4, 5].map((n) => ({
    label: `מקטע · מוכנות ${n}${n < 3 ? ' · חומר לפיתוח' : ' · טקסט לקריאה'}`,
    value: `${n} `,
    startOnly: true,
  })),
  { label: 'סיום מקטע', value: '^', startOnly: true },
  { label: 'הערה פרטית', value: '{}', startOnly: false },
  { label: 'בקשה ל־AI · ללא הפעלה', value: '[]', startOnly: false },
]
interface Menu {
  x: number
  y: number
  start: boolean
  selected: number
}

export function WritingSyntaxPlugin({
  annotations,
  showSyntax = false,
}: {
  annotations: Annotation[]
  showSyntax?: boolean
}) {
  const [editor] = useLexicalComposerContext()
  const [menu, setMenu] = useState<Menu | null>(null)
  const menuRef = useRef<Menu | null>(null)
  const dismissed = useRef<string | null>(null)
  const highlightName = useRef(`writing-${Math.random().toString(36).slice(2)}`).current
  const previousAnnotations = useRef(annotations)
  const [issues, setIssues] = useState<string[]>([])
  const showSyntaxRef = useRef(showSyntax)
  const refreshPresentation = useRef<() => void>(() => {})
  useEffect(() => {
    showSyntaxRef.current = showSyntax
    refreshPresentation.current()
  }, [showSyntax])
  const setCurrentMenu = (value: Menu | null) => {
    menuRef.current = value
    setMenu(value)
  }

  useEffect(() => {
    const before = previousAnnotations.current
    previousAnnotations.current = annotations
    if (before === annotations) {
      return
    }
    editor.update(() => {
      const ast = $snapshot()
      const updated = applyReadinessAnnotationChanges(ast, before, annotations)
      updated.blocks.forEach((block, index) => {
        if (block === ast.blocks[index] || block.type !== 'heading') {
          return
        }
        const node = $getRoot()
          .getChildren()
          .find((n) => n instanceof CustomHeadingNode && n.getBlockId() === block.id)
        if (!(node instanceof CustomHeadingNode)) {
          return
        }
        const oldText = node.getTextContent()
        const title = parseSectionOpening(oldText)?.title
        if (!title) {
          return
        }
        let skip = oldText.indexOf(title)
        for (const child of node.getChildren()) {
          if (!skip || !$isTextNode(child)) {
            break
          }
          const length = child.getTextContentSize()
          if (length <= skip) {
            skip -= length
            child.remove()
          } else {
            child.spliceText(0, skip, '')
            skip = 0
          }
        }
        const prefix = getBlockText(block).slice(0, getBlockText(block).indexOf(title))
        const text = $createTextNode(prefix)
        const first = node.getFirstChild()
        if (first) {
          first.insertBefore(text)
        } else {
          node.append(text)
        }
      })
    })
  }, [annotations, editor])

  useEffect(() => {
    const remove = editor.registerNodeTransform(CustomParagraphNode, (node) => {
      if (
        editor.isComposing() ||
        node.getParent()?.getKey() !== $getRoot().getKey() ||
        !/^\^(?:[1-5])?\s/.test(node.getTextContent())
      ) {
        return
      }
      const ast = $snapshot()
      const index = ast.blocks.findIndex((b) => b.id === node.getBlockId())
      if (index < 0) {
        return
      }
      // A candidate heading must be outside notes and code, including after a multiline paste.
      const candidate = {
        ...ast.blocks[index],
        type: 'heading' as const,
        level: 3 as const,
        content: [{ type: 'text' as const, text: node.getTextContent() + ' ' + '…' }],
      }
      if (
        !analyzeWritingSyntax([...ast.blocks.slice(0, index), candidate]).sections.some(
          (s) => s.heading.id === node.getBlockId(),
        )
      ) {
        return
      }
      const heading = $createCustomHeadingNode(node.getBlockId(), inferSectionLevel(ast.blocks, index))
      heading.setFormat(node.getFormatType()).setDirection(node.getDirection()).setIndent(node.getIndent())
      heading.append(...node.getChildren())
      node.replace(heading)
    })
    const removeText = editor.registerNodeTransform(TextNode, (node) => {
      const parent = node.getParent()
      if (parent instanceof CustomParagraphNode && /^\^(?:[1-5])?\s/.test(parent.getTextContent())) {
        parent.markDirty()
      }
    })
    return () => {
      remove()
      removeText()
    }
  }, [editor])

  useEffect(() => {
    let frame = 0
    let documentDirty = true
    let layoutDirty = true
    let analysis = analyzeWritingSyntax([])
    const styled = new Set<HTMLElement>()
    const markers: { range: Range; blockId: string; boundary?: HTMLElement; heading?: HTMLElement; title?: Range }[] =
      []
    const clearStyles = () => {
      styled.forEach((el) => {
        if (el.dataset.writingReadiness !== undefined) {
          el.removeAttribute('aria-description')
          for (const name of [
            'prefix-width',
            'prefix-slot',
            'frame-left',
            'frame-width',
            'badge-left',
            'divider-left',
          ]) {
            el.style.removeProperty(`--writing-${name}`)
          }
        }
        delete el.dataset.writingKind
        delete el.dataset.writingBoundary
        delete el.dataset.writingReadiness
        delete el.dataset.writingPrefix
        delete el.dataset.writingMarker
      })
      styled.clear()
    }
    const style = document.createElement('style')
    style.textContent = `::highlight(${highlightName}-note){background:var(--writing-note-highlight,var(--editor-note-bg))}::highlight(${highlightName}-request){background:var(--writing-request-highlight,var(--editor-request-bg))}::highlight(${highlightName}-markers){color:transparent;text-shadow:none}`
    document.head.append(style)
    const registry = (CSS as any).highlights
    const HighlightClass = (window as any).Highlight
    const paint = () => {
      const state = editor.getEditorState()
      if (documentDirty) {
        documentDirty = false
        layoutDirty = true
        const ast = lexicalToAst(state.toJSON(), { lang: 'mixed', dir: 'rtl' })
        analysis = analyzeWritingSyntax(ast.blocks)
        const blocksById = new Map(ast.blocks.map((b) => [b.id, b]))
        const sectionOpenings = new Map(
          analysis.sections.map(({ heading }) => [heading.id, parseSectionOpening(getBlockText(heading))!]),
        )
        // The scanner indexes list-item ranges, not list containers. Promote only
        // wholly private subtrees, so mixed lists keep their public text unchanged.
        const privateLists = new Map<string, WritingKind>()
        const indexItems = (items: ListItem[]): (WritingKind | undefined)[] =>
          items.map((item) => {
            const ranges = analysis.ranges.get(item.id)
            const range = ranges?.length === 1 ? ranges[0] : undefined
            const kind = range?.start === 0 && range.end === getInlineText(item.content).length ? range.kind : undefined
            const children = indexItems(item.children ?? [])
            if (kind && children.every((child) => child === kind)) {
              privateLists.set(item.id, kind)
              return kind
            }
            return undefined
          })
        for (const block of ast.blocks) {
          if (block.type === 'list') {
            const kinds = indexItems(block.items)
            const kind = kinds[0]
            if (kind && kinds.every((item) => item === kind)) {
              privateLists.set(block.id, kind)
            }
          }
        }
        const nextIssues = analysis.issues.map((i) => i.message)
        setIssues((previous) => (JSON.stringify(previous) === JSON.stringify(nextIssues) ? previous : nextIssues))
        clearStyles()
        markers.length = 0
        const root = editor.getRootElement()
        if (!root) {
          return
        }
        const noteRanges: Range[] = [],
          requestRanges: Range[] = []
        root.querySelectorAll<HTMLElement>('[data-block-id]').forEach((el) => {
          const id = el.dataset.blockId!
          const block = blocksById.get(id)
          if (analysis.boundaryBlocks.has(id)) {
            el.dataset.writingBoundary = 'true'
            styled.add(el)
          }
          const opening = sectionOpenings.get(id)
          if (opening) {
            const rating = opening.readiness
            el.dataset.writingReadiness = rating ? String(rating) : '—'
            el.dataset.writingPrefix = 'visible'
            el.setAttribute(
              'aria-description',
              rating
                ? `מקטע, מוכנות ${rating} מתוך 5, ${rating < 3 ? 'חומר לפיתוח' : 'טקסט לקריאה'}`
                : 'מקטע ללא דירוג מוכנות',
            )
            styled.add(el)
          }
          const ranges = analysis.ranges.get(id)
          const privateKind = privateLists.get(id) ?? (analysis.hiddenBlocks.has(id) ? ranges?.[0]?.kind : undefined)
          // The highest private ancestor owns the rule and typography; descendants
          // inherit them instead of adding borders or repeatedly shrinking text.
          if (privateKind && !el.parentElement?.closest('[data-writing-kind]')) {
            el.dataset.writingKind = privateKind
            const text = block && getBlockText(block).trim()
            if (text === '^{' || text === '^[') {
              el.dataset.writingMarker = 'open'
            } else if (text === '}' || text === ']') {
              el.dataset.writingMarker = 'close'
            }
            styled.add(el)
          }
          const boundary = analysis.boundaryBlocks.has(id)
          if (!ranges?.length && !boundary && !opening) {
            return
          }
          if (!registry || !HighlightClass) {
            return
          }
          const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
          const texts: Text[] = []
          while (walker.nextNode()) {
            texts.push(walker.currentNode as Text)
          }
          const domRange = (start: number, stop: number): Range | undefined => {
            let offset = 0
            const range = document.createRange()
            let started = false
            for (const text of texts) {
              const end = offset + text.length
              if (!started && start < end) {
                range.setStart(text, Math.max(0, start - offset))
                started = true
              }
              if (started && stop <= end) {
                range.setEnd(text, Math.max(0, stop - offset))
                return range
              }
              offset = end
            }
          }
          if (boundary && block) {
            const start = getBlockText(block).indexOf('^^')
            const range = domRange(start, start + 2)
            if (range) {
              markers.push({ range, blockId: id, boundary: el })
            }
          }
          if (opening && block) {
            const text = getBlockText(block)
            const start = text.indexOf(opening.title)
            const range = domRange(0, start)
            const title = domRange(start, text.length)
            if (range && title) {
              markers.push({ range, blockId: id, heading: el, title })
            }
          }
          for (const source of ranges ?? []) {
            const range = domRange(source.start, source.end)
            if (range) {
              ;(source.kind === 'note' ? noteRanges : requestRanges).push(range)
            }
          }
          for (const source of analysis.delimiters.get(id) ?? []) {
            const range = domRange(source.start, source.end)
            if (range) {
              markers.push({ range, blockId: id })
            }
          }
        })
        if (registry && HighlightClass) {
          registry.set(`${highlightName}-note`, new HighlightClass(...noteRanges))
          registry.set(`${highlightName}-request`, new HighlightClass(...requestRanges))
        }
      }
      if (registry && HighlightClass) {
        if (layoutDirty) {
          layoutDirty = false
          // Measure only after content, font or container-size changes. Batch
          // reads/writes so moving the caret never forces heading layout.
          const headings = markers.flatMap(({ range, heading, title }) => {
            if (!heading || !title) {
              return []
            }
            const width = range.getBoundingClientRect().width
            const style = getComputedStyle(heading)
            const inset = parseFloat(style.getPropertyValue('--writing-title-inset'))
            const badgeWidth = parseFloat(style.getPropertyValue('--writing-badge-width'))
            return [
              {
                title,
                heading,
                width,
                inset,
                badgeWidth,
                slot: Math.max(inset, width),
                rtl: style.direction === 'rtl',
              },
            ]
          })
          headings.forEach(({ heading, width, slot }) => {
            heading.style.setProperty('--writing-prefix-width', `${width}px`)
            heading.style.setProperty('--writing-prefix-slot', `${slot}px`)
          })
          const positions = headings.map(({ title, heading, inset, badgeWidth, rtl }) => {
            const text = title.getBoundingClientRect()
            const box = heading.getBoundingClientRect()
            const left = text.left - inset - (rtl ? badgeWidth : 0) - box.left
            const width = text.width + inset * 2 + badgeWidth
            return {
              heading,
              left,
              width,
              badgeLeft: rtl ? left + 1 : left + width - badgeWidth - 1,
              dividerLeft: rtl ? badgeWidth : width - badgeWidth - 2,
            }
          })
          positions.forEach(({ heading, left, width, badgeLeft, dividerLeft }) => {
            heading.style.setProperty('--writing-frame-left', `${left}px`)
            heading.style.setProperty('--writing-frame-width', `${width}px`)
            heading.style.setProperty('--writing-badge-left', `${badgeLeft}px`)
            heading.style.setProperty('--writing-divider-left', `${dividerLeft}px`)
          })
        }
        const root = editor.getRootElement()
        const selection = window.getSelection()
        const selected =
          root?.contains(document.activeElement) && selection?.rangeCount ? selection.getRangeAt(0) : null
        const issueBlocks = new Set(analysis.issues.map((issue) => issue.blockId))
        const hidden: Range[] = []
        for (const { range, blockId, boundary, heading } of markers) {
          // Inclusive endpoints reveal syntax when the caret reaches it,
          // or when a forward/backward selection touches or crosses it.
          const conceal =
            !showSyntaxRef.current &&
            !issueBlocks.has(blockId) &&
            (!selected ||
              !(
                range.comparePoint(selected.startContainer, selected.startOffset) <= 0 &&
                range.comparePoint(selected.endContainer, selected.endOffset) >= 0
              ))
          if (conceal) {
            hidden.push(range)
          }
          if (boundary) {
            const value = conceal ? 'collapsed' : 'true'
            if (boundary.dataset.writingBoundary !== value) {
              boundary.dataset.writingBoundary = value
            }
          }
          if (heading) {
            const value = conceal ? 'collapsed' : 'visible'
            if (heading.dataset.writingPrefix !== value) {
              heading.dataset.writingPrefix = value
            }
          }
        }
        const highlight = new HighlightClass(...hidden)
        highlight.priority = 1
        registry.set(`${highlightName}-markers`, highlight)
      }
      state.read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection) || !selection.isCollapsed() || editor.isComposing()) {
          setCurrentMenu(null)
          return
        }
        const node = selection.anchor.getNode()
        if (!$isTextNode(node)) {
          setCurrentMenu(null)
          return
        }
        const offset = selection.anchor.offset
        const before = node.getTextContent().slice(0, offset)
        const signature = `${node.getKey()}:${offset}`
        if (!before.endsWith('^') || /[\\^]$/.test(before.slice(0, -1)) || dismissed.current === signature) {
          setCurrentMenu(null)
          return
        }
        const block = node.getTopLevelElement()
        const blockId = block && 'getBlockId' in block ? (block as CustomParagraphNode).getBlockId() : undefined
        if (blockId && analysis.ranges.has(blockId)) {
          setCurrentMenu(null)
          return
        }
        if (
          before.includes('`') ||
          node.getParent()?.getType() === 'link' ||
          node.getParent()?.getType() === 'encrypted'
        ) {
          setCurrentMenu(null)
          return
        }
        const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect()
        if (!rect) {
          return
        }
        const previous = menuRef.current
        setCurrentMenu({
          x: Math.min(window.innerWidth - 300, Math.max(8, rect.left)),
          y: Math.min(window.innerHeight - 350, rect.bottom + 8),
          start: before === '^' && node.getPreviousSiblings().length === 0 && node.getParent() === block,
          selected: previous?.selected ?? 0,
        })
      })
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(paint)
    }
    refreshPresentation.current = schedule
    const invalidateLayout = () => {
      layoutDirty = true
      schedule()
    }
    const resizeObserver = new ResizeObserver(invalidateLayout)
    // The trailing badge is generated content, outside Lexical's document.
    // Clicking it moves the caret to the real prefix for normal keyboard editing.
    const editReadiness = (event: PointerEvent) => {
      if (event.button !== 0 || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      const heading =
        event.target instanceof Element ? event.target.closest<HTMLElement>('[data-writing-prefix="collapsed"]') : null
      if (!heading) {
        return
      }
      const box = heading.getBoundingClientRect()
      const style = getComputedStyle(heading)
      const left = box.left + parseFloat(style.getPropertyValue('--writing-badge-left'))
      if (event.clientX < left || event.clientX > left + parseFloat(style.getPropertyValue('--writing-badge-width'))) {
        return
      }
      event.preventDefault()
      editor.update(() => {
        const node = $getNearestNodeFromDOMNode(heading)
        if ($isElementNode(node)) {
          node.selectStart()
        }
      })
    }
    document.fonts.addEventListener('loadingdone', invalidateLayout)
    document.addEventListener('selectionchange', schedule)
    const removeRootListener = editor.registerRootListener((root, previous) => {
      resizeObserver.disconnect()
      if (root) {
        resizeObserver.observe(root)
      }
      invalidateLayout()
      previous?.removeEventListener('focusin', schedule)
      previous?.removeEventListener('focusout', schedule)
      previous?.removeEventListener('pointerdown', editReadiness)
      root?.addEventListener('focusin', schedule)
      root?.addEventListener('focusout', schedule)
      root?.addEventListener('pointerdown', editReadiness)
    })
    const remove = editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
      // Selection changes only update presentation. Reuse analysis and DOM ranges
      // until Lexical reports a content/formatting change, including undo or reload.
      documentDirty ||= dirtyElements.size > 0 || dirtyLeaves.size > 0
      schedule()
    })
    schedule()
    return () => {
      remove()
      removeRootListener()
      resizeObserver.disconnect()
      document.fonts.removeEventListener('loadingdone', invalidateLayout)
      document.removeEventListener('selectionchange', schedule)
      refreshPresentation.current = () => {}
      cancelAnimationFrame(frame)
      style.remove()
      registry?.delete(`${highlightName}-note`)
      registry?.delete(`${highlightName}-request`)
      registry?.delete(`${highlightName}-markers`)
      clearStyles()
    }
  }, [editor, highlightName])

  const insert = (value: string) => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) {
        return
      }
      selection.insertText(value)
      if (value === '{}' || value === '[]') {
        const node = selection.anchor.getNode(),
          offset = selection.anchor.offset
        if ($isTextNode(node)) {
          node.select(offset - 1, offset - 1)
        }
      }
    })
    setCurrentMenu(null)
  }
  useEffect(
    () =>
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if (!event.isComposing && (event.key === '}' || event.key === ']')) {
            const selection = $getSelection()
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
              const node = selection.anchor.getNode(),
                offset = selection.anchor.offset
              if ($isTextNode(node) && node.getTextContent()[offset] === event.key) {
                const ast = $snapshot(),
                  block = node.getTopLevelElement()
                const id = block && 'getBlockId' in block ? (block as CustomParagraphNode).getBlockId() : ''
                if (analyzeWritingSyntax(ast.blocks).ranges.has(id)) {
                  event.preventDefault()
                  node.select(offset + 1, offset + 1)
                  return true
                }
              }
            }
          }
          const current = menuRef.current
          if (!current || event.isComposing) {
            return false
          }
          const visible = choices.filter((c) => current.start || !c.startOnly)
          if (event.key === 'Escape') {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              dismissed.current = `${selection.anchor.key}:${selection.anchor.offset}`
            }
            event.preventDefault()
            setCurrentMenu(null)
            return true
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setCurrentMenu({
              ...current,
              selected: (current.selected + (event.key === 'ArrowDown' ? 1 : visible.length - 1)) % visible.length,
            })
            return true
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            insert(visible[current.selected % visible.length].value)
            return true
          }
          if (event.key === '{' || event.key === '[') {
            event.preventDefault()
            insert(event.key === '{' ? '{}' : '[]')
            return true
          }
          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor],
  )

  return (
    <>
      {issues.length > 0 && (
        <div className="writing-syntax-status" role="status">
          טיוטה: {[...new Set(issues)].join(' · ')}
        </div>
      )}
      {menu &&
        createPortal(
          <div
            className="writing-syntax-menu"
            role="listbox"
            aria-label="תחביר כתיבה"
            dir="rtl"
            style={{ left: menu.x, top: Math.max(8, menu.y) }}
          >
            {choices
              .filter((c) => menu.start || !c.startOnly)
              .map((choice, index) => (
                <button
                  key={choice.value}
                  type="button"
                  role="option"
                  aria-selected={index === menu.selected}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insert(choice.value)}
                >
                  <code dir="ltr">^{choice.value}</code>
                  <span>{choice.label}</span>
                </button>
              ))}
          </div>,
          document.body,
        )}
    </>
  )
}
